import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { ReportProblemRounded } from "@mui/icons-material";
import React, { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import useScheme from "../../providers/scheme_provider";
import { SchemeOtherDetailsInputs } from "../../models/scheme_form";
import { SchemeDetailsResponse } from "../../models/scheme_details";
import Loading from "../../../../components/loading/loading";
import { useNavigate } from "react-router-dom";
import HTMLEditor from "../../../../components/html_editor/html_editor";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";
import RequiredMark from "../../../../components/required_mark/required_mark";

export default function SchemeDetails({
  data,
  isError,
  errorMessage,
  onSave,
  openEligibility,
  readOnly,
  submitting = false,
}: {
  data: SchemeDetailsResponse | undefined;
  isError: boolean;
  errorMessage?: string;
  onSave: (data: SchemeOtherDetailsInputs) => void;
  openEligibility: () => void;
  readOnly: boolean;
  submitting?: boolean;
}) {
  const { schemeMaster } = useScheme();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SchemeOtherDetailsInputs>({});

  useEffect(() => {
    if (data) {
      setValue("name", data?.details?.name);
      setValue("details", data?.details?.details);
      setValue("benefits", data?.details?.benefits);
      setValue("exclusions", data?.details?.exclusions);
      setValue("applicationProcess", data?.details?.processModeId);
      setValue("applicationProcessDetails", data?.details?.process_details);
      setValue("eligibility", data?.details?.eligibility);
      setValue("documentRequired", data?.details?.documents_required);
      setValue("governmentTypeId", data?.details?.governmentTypeId);
      setValue("providerId", data?.providerId ?? null);
    }
  }, [data]);

  const onSubmit: SubmitHandler<SchemeOtherDetailsInputs> = (data) =>
    onSave(data);

  function fieldValue({ title, value }: { title: string; value: string }) {
    return (
      <Box>
        <Typography variant="body2" color="primary.main">
          {title}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {value}
        </Typography>
      </Box>
    );
  }

  if (isError)
    return (
      <Box
        height={300}
        display="flex"
        flexDirection="column"
        gap={1}
        justifyContent="center"
        alignItems="center"
      >
        <ReportProblemRounded sx={{ color: greyEnabled, fontSize: 32 }} />
        <Typography color={greyEnabled}>
          {errorMessage || "Something went wrong while loading the scheme details."}
        </Typography>
      </Box>
    );
  if (!data)
    return (
      <Box
        height={300}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Loading />
      </Box>
    );

  return (
    <Box p={2} pt={3}>
      <Box pb={3}>
        <Typography variant="subtitle1" fontWeight={700} color={black}>
          Scheme Details
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3} alignItems={"flex-start"}>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main">
              Scheme Name<RequiredMark />
            </Typography>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Enter scheme name" }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  size="small"
                  disabled={readOnly}
                  value={value || ""}
                  onChange={onChange}
                  placeholder="Enter scheme name"
                  sx={{
                    mt: 0.5,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#ABA0AB" },
                      "&:hover fieldset": { borderColor: purple },
                      "&.Mui-focused fieldset": {
                        borderColor: purple,
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              )}
            />
            {!!errors.name && (
              <Typography variant="caption" color="error">
                {errors.name?.message}
              </Typography>
            )}
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main">
              Type of Government
            </Typography>
            <Controller
              name="governmentTypeId"
              control={control}
              disabled={readOnly}
              render={({ field: { onChange, value } }) => (
                <Box mt={1}>
                  {schemeMaster?.schemeGovernment?.map((government) => (
                    <Chip
                      key={government.id}
                      disabled={readOnly}
                      onClick={() => onChange(government.id)}
                      label={government.value}
                      variant={value === government.id ? "filled" : "outlined"}
                      color="primary"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            />
          </Grid>
          <Grid item md={12}>
            {fieldValue({
              title: "Type of Category",
              value: data.details.categories.join(", "),
            })}
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main">
              Provider
            </Typography>
            <Controller
              name="providerId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  fullWidth
                  disabled={readOnly}
                  options={schemeMaster?.schemeProviders || []}
                  getOptionLabel={(option) => option.value}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    schemeMaster?.schemeProviders?.find((p) => p.id === value) ||
                    null
                  }
                  onChange={(_, option) => onChange(option?.id ?? null)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.logo && (
                        <Box
                          component="img"
                          src={option.logo}
                          alt=""
                          sx={{ width: 20, height: 20, objectFit: "contain", mr: 1 }}
                        />
                      )}
                      {option.value}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select provider (Optional)"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                />
              )}
            />
            {data?.providerLogo && (
              <Box
                component="img"
                src={data.providerLogo}
                alt="Provider logo"
                mt={1}
                sx={{ width: 40, height: 40, objectFit: "contain", borderRadius: 1, border: "1px solid #ECECEC" }}
              />
            )}
          </Grid>
          {/* <Grid item md={12}>
            <FormControlLabel
              label="Mark scheme as top scheme"
              control={<Checkbox checked readOnly />}
            />
          </Grid> */}
          {!readOnly && (
            <Grid item md={12}>
              <Button
                variant="outlined"
                size="large"
                onClick={openEligibility}
                sx={{
                  borderColor: purple,
                  color: purple,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: purple, bgcolor: purpleLight },
                }}
              >
                Modify Eligibility Criteria
              </Button>
            </Grid>
          )}
          <Grid item md={12}>
            <Divider />
          </Grid>
          <Grid item md={6}>
            <Box>
              <Typography variant="body2" color="primary.main" mb={0.5}>
                Scheme Application Process<RequiredMark />
              </Typography>
              <Controller
                name="applicationProcess"
                control={control}
                disabled={readOnly}
                rules={{ required: "Select the process mode*" }}
                render={({ field: { onChange, value } }) => (
                  <Box>
                    {schemeMaster?.schemeProcessMode?.map((process) => (
                      <Chip
                        key={process.id}
                        disabled={readOnly}
                        onClick={() => onChange(process.id)}
                        label={process.value}
                        variant={value === process.id ? "filled" : "outlined"}
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                    ))}
                    {!schemeMaster?.schemeProcessMode?.length && (
                      <Typography variant="body2" color="text.secondary">
                        No application process options are configured.
                      </Typography>
                    )}
                  </Box>
                )}
              />
              {!!errors.applicationProcess && (
                <Typography variant="caption" color="error">
                  {errors.applicationProcess?.message}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Application Process Details
            </Typography>
            <Controller
              name="applicationProcessDetails"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Application Process Details (Optional)"
                  readOnly={readOnly}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Details<RequiredMark />
            </Typography>
            <Controller
              name="details"
              control={control}
              rules={{ required: "Enter scheme details" }}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter scheme details"
                  readOnly={readOnly}
                />
              )}
            />
            {!!errors.details && (
              <Typography variant="caption" color="error">
                {errors.details?.message}
              </Typography>
            )}
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Benefits
            </Typography>
            <Controller
              name="benefits"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Benefits (Optional)"
                  readOnly={readOnly}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Exclusions
            </Typography>
            <Controller
              name="exclusions"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Exclusions (Optional)"
                  readOnly={readOnly}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Eligibility Description
            </Typography>
            <Controller
              name="eligibility"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter eligibility description (Optional)"
                  readOnly={readOnly}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Documents Required
            </Typography>
            <Controller
              name="documentRequired"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter documents required (Optional)"
                  readOnly={readOnly}
                />
              )}
            />
          </Grid>
        </Grid>
        {!readOnly && (
          <Box pt={4} display="flex" justifyContent="space-between">
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: purple,
                color: purple,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disableElevation
              type="submit"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : undefined
              }
              sx={{
                bgcolor: purple,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Save changes (This language)
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
}
